/*
 * lp-product-finder
 * ------------------------------------------------------------------
 * Inline shade finder card: intro -> stepped questions (visual dot progress) ->
 * a result that shows the recommended shades INSIDE the finder (display-only),
 * then "See my kit" dispatches `lpv2-finder:complete` with the picked variant ids.
 * The LP Products section selects exactly those.
 *
 * Steps are hardcoded stubs; the recommendation is a stub (first N available
 * shades). Phase 2 makes steps metaobject-driven and the picks a real scored set.
 * The finder reads the placed shades (lp-bundle-item) as its candidate pool.
 *
 * Idempotent load guard: this file is enqueued once per standalone finder section AND once
 * by lpv2-products when a shade-assistant block is present. Multiple identical <script src> tags
 * each execute, so without this IIFE + early-return the top-level declarations would throw
 * "already declared". First load wins; later loads are a clean no-op.
 */
(function () {
  if (customElements.get('lp-product-finder')) return;

  const FINDER_RECOMMEND_COUNT = 3;
  // Duration of the "computing" beat between the last question and the result.
  const FINDER_COMPUTE_MS = 1300;
  const FINDER_COMPUTE_PHASE_MS = 1300;

  class LpProductFinder extends HTMLElement {
  connectedCallback() {
    // Init once. The finder is moved in the DOM after a run (LpCart.moveFinderToTop),
    // which disconnects + reconnects this element and would re-fire connectedCallback —
    // re-binding a second set of option listeners (the originals survive the move), so a
    // single tap would fire choose() twice and skip a step on the next run. The instance
    // (and its listeners) persist across the move, so re-initialising is never needed.
    if (this._initialized) return;
    this._initialized = true;

    this.intro = this.querySelector('[data-finder-intro]');
    this.panel = this.querySelector('[data-finder-panel]');
    this.flow = this.querySelector('[data-finder-flow]');
    this.result = this.querySelector('[data-finder-result]');
    this.dotsEl = this.querySelector('[data-finder-dots]');
    this.resultHeadingEl = this.querySelector('[data-finder-result-heading]');
    this.resultHeadingTpl = this.resultHeadingEl ? this.resultHeadingEl.innerHTML : '';
    this.twinkleEl = this.querySelector('[data-finder-twinkle]');
    this.isAssist = this.classList.contains('lpv2-finder--assist');
    this.doneBlock = this.querySelector('.lpv2-finder__done');
    this.reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.backBtn = this.querySelector('[data-finder-back]');
    this.computeEl = this.querySelector('[data-finder-compute]');
    this.computeStatusEl = this.querySelector('[data-finder-compute-status]');
    this.computePhases = this.computeStatusEl
      ? (this.computeStatusEl.getAttribute('data-phases') || this.computeStatusEl.textContent)
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    this.answers = {};
    this.answerMeta = {}; // axis -> { dim, label, swatch } for the result pills
    this.index = 0;
    this.pickIds = [];
    this.recommendedIds = [];
    this.loadOutcomes();

    // One progress dot per step.
    if (this.dotsEl) {
      this.dotsEl.innerHTML = '';
      this.steps.forEach(() => this.dotsEl.appendChild(document.createElement('i')));
    }

    const startBtn = this.querySelector('[data-finder-start]');
    if (startBtn) startBtn.addEventListener('click', () => this.start());

    this.querySelectorAll('.lpv2-finder__opt').forEach((opt) => {
      opt.addEventListener('click', () => this.choose(opt));
    });

    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => {
        if (this.index <= 0) this.toIntro();
        else this.goTo(this.index - 1);
      });
    }

    const applyBtn = this.querySelector('[data-finder-apply]');
    if (applyBtn) applyBtn.addEventListener('click', () => this.apply());

    const restartBtn = this.querySelector('[data-finder-restart]');
    if (restartBtn) restartBtn.addEventListener('click', () => this.restart());

    this.warmOptionImages();

    // Keep the done summary scaled to the locked card as the viewport / fonts change.
    if (this.isAssist) {
      this._onResize = () => this.fitDone();
      window.addEventListener('resize', this._onResize);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fitDone());
    }
  }

  // Step images are lazy + inside hidden (display:none) step groups, so they don't fetch
  // until that step is revealed — causing a flash/lag mid-flow. Warm the HTTP cache for
  // every option image as the finder nears the viewport (not on initial load, to spare
  // the hero/LCP). On reveal the lazy <img> is then a cache hit = instant.
  warmOptionImages() {
    const imgs = Array.from(this.querySelectorAll('.lpv2-finder__opt-img'));
    if (!imgs.length) return;
    const warm = () => {
      imgs.forEach((img) => {
        const src = img.getAttribute('src');
        if (src) {
          const pre = new Image();
          pre.src = src;
        }
      });
    };
    if (!('IntersectionObserver' in window)) {
      warm();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          warm();
          io.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );
    io.observe(this);
  }

  // Outcomes lookup (skin_tone × eye_color × brow_contrast → recommended), read from the
  // embedded <script data-finder-outcomes> (the selected metaobject's results_json). If
  // absent, this.outcomes stays null and pickRecommended() uses its deterministic fallback.
  loadOutcomes() {
    this.outcomes = null;
    const embedded = this.querySelector('[data-finder-outcomes]');
    if (embedded && embedded.textContent.trim()) {
      try {
        this.outcomes = JSON.parse(embedded.textContent);
      } catch (e) {}
    }
  }

  setDots(reached) {
    if (!this.dotsEl) return;
    Array.from(this.dotsEl.children).forEach((d, i) => d.classList.toggle('is-on', i <= reached));
  }

  toIntro() {
    this.clearCompute();
    this.stopTwinkle();
    this.classList.remove('is-result');
    // is-open tracks whether the panel is expanded (vs the intro). The button variant gates
    // its card chrome on this, so going Back to the intro collapses it to the bare button
    // (is-engaged stays set — it only suppresses the entry beacon, which is one-way).
    this.classList.remove('is-open');
    if (this.panel) this.panel.hidden = true;
    if (this.intro) this.intro.hidden = false;
    this.index = 0;
  }

  start() {
    this.clearCompute();
    this.stopTwinkle();
    this.classList.remove('is-result');
    // Entry-attention beacon/drift draws the eye to the un-engaged finder; stop it for
    // good once the customer taps in (CSS gates on :not(.is-engaged)).
    this.classList.add('is-engaged');
    // is-open = panel expanded; the button variant shows its card chrome only while open.
    this.classList.add('is-open');
    // Don't reset the applied product layout just for re-opening the finder — the
    // current recommendations stay until a new run completes (applyFinderResult
    // clears the prior selection itself). Only "Start over" (restart) resets.
    this.answers = {};
    this.index = 0;
    if (this.intro) this.intro.hidden = true;
    if (this.panel) this.panel.hidden = false;
    if (this.result) this.result.hidden = true;
    if (this.flow) this.flow.hidden = false;
    this.goTo(0);
    this.centerInViewport();
  }

  restart() {
    this.clearCompute();
    this.stopTwinkle();
    this.classList.remove('is-result');
    this.dispatchReset();
    this.answers = {};
    this.index = 0;
    if (this.result) this.result.hidden = true;
    if (this.flow) this.flow.hidden = false;
    this.goTo(0);
  }

  // Present the done/result panel WITHOUT recomputing recommendations or dispatching a
  // completion. Used to consolidate the result onto the PRIMARY finder when the customer
  // actually ran a *different* instance (multiple shade-assistant entry points): the page
  // always scrolls back to the first card, and the matched-shade stack reorders beneath it,
  // so the first card must show the done summary regardless of which card was used.
  presentDone() {
    this.clearCompute();
    this.stopTwinkle();
    this.setDots(this.steps.length); // all filled
    this.classList.add('is-engaged'); // one-way: kill the entry beacon
    this.classList.add('is-open'); // panel expanded (chrome for the button variant)
    this.classList.add('is-result'); // result background
    if (this.intro) this.intro.hidden = true;
    if (this.panel) this.panel.hidden = false;
    if (this.flow) this.flow.hidden = true;
    if (this.result) this.result.hidden = false;
    if (this.isAssist) this.startDone();
    else this.startTwinkle();
  }

  // Bring the finder to the vertical centre of the viewport when the journey starts.
  // If the panel is taller than the viewport, top-align it (with a small margin) so its
  // top never scrolls off-screen. Honours prefers-reduced-motion (instant jump).
  //
  // Measure on the next frame, not synchronously: start() has just swapped the intro
  // (display:none) for the taller panel (display:block) and reset sibling layout. A
  // same-tick getBoundingClientRect() can read the pre-panel (intro-sized) box, which
  // centred the scroll on the intro instead of the revealed step. rAF defers until that
  // layout is committed so we measure the panel that's actually on screen.
  centerInViewport() {
    requestAnimationFrame(() => {
      const doc = document.documentElement;
      const margin = 24;
      const rect = this.getBoundingClientRect();
      const current = window.pageYOffset || doc.scrollTop || 0;
      const elemTop = rect.top + current;

      // Centre on the element's height AS IF its TALLEST step were showing (the step with the
      // most options — e.g. eye colour), not the current/first step. Otherwise the finder
      // centres on the short first view and drifts low when a taller step appears. Measure
      // each step's natural height by showing one at a time within this frame — synchronous,
      // so the browser never paints the intermediate states (no flash) — then restore.
      let centerH = rect.height;
      if (this.steps && this.steps.length) {
        const prevHidden = this.steps.map((s) => s.hidden);
        let tallestStep = 0;
        this.steps.forEach((s, i) => {
          this.steps.forEach((o, j) => (o.hidden = j !== i));
          if (s.offsetHeight > tallestStep) tallestStep = s.offsetHeight;
        });
        this.steps.forEach((s, i) => (s.hidden = prevHidden[i]));
        const currentStep = this.steps[this.index] ? this.steps[this.index].offsetHeight : 0;
        centerH = rect.height - currentStep + tallestStep;
      }

      let target;
      if (centerH >= window.innerHeight - margin * 2) {
        target = elemTop - margin;
      } else {
        target = elemTop - (window.innerHeight - centerH) / 2;
      }
      const maxScroll = (doc.scrollHeight || 0) - window.innerHeight;
      target = Math.max(0, Math.min(target, Math.max(0, maxScroll)));

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.scrollTo(0, target);
        return;
      }
      this.smoothScrollTo(target, 420);
    });
  }

  // Fast-but-soft ease-out tween (easeOutCubic): quick off the mark, gentle settle.
  smoothScrollTo(target, duration) {
    const doc = document.documentElement;
    const start = window.pageYOffset || doc.scrollTop || 0;
    const dist = target - start;
    if (Math.abs(dist) < 2) return;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    let t0 = null;
    const step = (now) => {
      if (t0 === null) t0 = now;
      const p = Math.min(1, (now - t0) / duration);
      window.scrollTo(0, start + dist * ease(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  goTo(index) {
    this.index = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, i) => (step.hidden = i !== this.index));
    this.setDots(this.index);
    if (this.backBtn) {
      this.backBtn.hidden = false;
      // Step 0 back closes the finder (returns to intro), so it reads "Close"; later steps "Back".
      const label = this.index <= 0 ? this.backBtn.dataset.labelClose : this.backBtn.dataset.labelBack;
      if (label) this.backBtn.textContent = '‹ ' + label;
    }
    const active = this.steps[this.index];
    const question = active && active.querySelector('.lpv2-finder__q');
    // preventScroll: the browser's default focus-scroll would jump the page and fight
    // the centerInViewport() tween (and cause unwanted jumps on step navigation).
    if (question) question.focus({ preventScroll: true });
  }

  choose(opt) {
    const step = opt.closest('[data-step]');
    if (step) {
      const axis = step.dataset.axis;
      this.answers[axis] = opt.dataset.value;
      const sw = opt.querySelector('.lpv2-finder__sw');
      this.answerMeta[axis] = {
        dim: step.dataset.dim || '',
        label: (opt.textContent || '').replace('›', '').trim(),
        swatch: sw ? getComputedStyle(sw).backgroundColor : '',
      };
    }
    if (this.index < this.steps.length - 1) {
      this.goTo(this.index + 1);
    } else {
      this.runCompute(() => this.showResult());
    }
  }

  // The "calculating" beat: hide the flow, show the loader with the status line
  // for ~1.3s, then reveal the result. Skipped under reduced motion (and if the
  // compute element is absent) — straight to the result.
  runCompute(onDone) {
    this.clearCompute();
    this.setDots(this.steps.length); // all filled while computing
    if (this.flow) this.flow.hidden = true;
    if (this.result) this.result.hidden = true;

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !this.computeEl) {
      onDone();
      return;
    }

    this.computeEl.hidden = false;
    this.classList.add('is-computing'); // sweeps the card-wide shimmer (removed in clearCompute)
    const statusEl = this.computeStatusEl;
    const phases = this.computePhases;
    if (statusEl && phases.length) {
      statusEl.textContent = phases[0];
      statusEl.style.opacity = '1';
      let pi = 0;
      this.computeCycle = window.setInterval(() => {
        pi += 1;
        if (pi >= phases.length) return;
        statusEl.style.opacity = '0';
        window.setTimeout(() => {
          if (statusEl) {
            statusEl.textContent = phases[pi];
            statusEl.style.opacity = '1';
          }
        }, 170);
      }, FINDER_COMPUTE_PHASE_MS);
    }

    this.computeDone = window.setTimeout(() => {
      this.clearCompute();
      if (this.computeEl) this.computeEl.hidden = true;
      onDone();
    }, FINDER_COMPUTE_MS);
  }

  clearCompute() {
    this.classList.remove('is-computing'); // stop the card shimmer on done / back / restart
    if (this.computeCycle) {
      window.clearInterval(this.computeCycle);
      this.computeCycle = null;
    }
    if (this.computeDone) {
      window.clearTimeout(this.computeDone);
      this.computeDone = null;
    }
    if (this.computeEl) this.computeEl.hidden = true;
    // Reset the status to the first phase so the next run starts fresh.
    if (this.computeStatusEl && this.computePhases.length) {
      this.computeStatusEl.textContent = this.computePhases[0];
      this.computeStatusEl.style.opacity = '1';
    }
  }

  showResult() {
    this.setDots(this.steps.length); // all filled
    if (this.flow) this.flow.hidden = true;
    if (this.result) this.result.hidden = false;
    this.classList.add('is-result'); // swaps the card to the result background

    // The full ordered recommended set [{ variantId, text }] for the result layout.
    // No preview list here — the CTA reveals them in the LP Products section.
    this.recommended = this.recommend();

    if (this.isAssist) {
      // Collapse to the done summary; the results are auto-applied below (dispatchComplete),
      // so there's no tease / apply tap — just the matched card + re-start.
      this.startDone();
    } else {
      // "{count} shades would suit you…" — count = how many shades suit (the match set).
      if (this.resultHeadingEl && this.resultHeadingTpl) {
        this.resultHeadingEl.innerHTML = this.resultHeadingTpl.replace(
          /\{count\}/g,
          this.recommended.length,
        );
        this.resultHeadingEl.setAttribute('tabindex', '-1');
        this.resultHeadingEl.focus();
      }
      this.startTwinkle();
    }

    // Apply the result to the LP Products section right away (no scroll) — the
    // grid re-lays-out off-screen the moment this result screen appears.
    this.dispatchComplete();
  }

  // Scale the done summary so it always fits the locked 16/6 card. Content keeps its
  // natural size; only the whole block shrinks when it would overflow (longer languages).
  fitDone() {
    const box = this.doneBlock;
    if (!box || !this.classList.contains('is-result')) return;
    const card = this.querySelector('.lpv2-finder__card');
    if (!card) return;
    box.style.transform = 'none';
    const s = Math.min(
      1,
      (card.clientHeight - 2) / box.scrollHeight,
      (card.clientWidth - 2) / box.scrollWidth,
    );
    box.style.transform = s < 1 ? `scale(${s})` : 'none';
  }

  // The done summary is static (RE-START + two lines) — just scale it to the locked card.
  startDone() {
    requestAnimationFrame(() => this.fitDone());
  }

  // Placed, selectable shade cards on the page.
  candidates() {
    return Array.from(document.querySelectorAll('lp-bundle-item')).filter((el) => {
      const input = el.querySelector('input');
      return input && !input.disabled;
    });
  }

  // Ordered recommended set [{ variantId, text }]. Looks up the combo (skin_tone ×
  // eye_color × brow_contrast) in the outcomes JSON, keeping only variants actually on
  // the page. Falls back to the deterministic hash mock if the data isn't available.
  recommend() {
    const onPage = new Set(this.candidates().map((el) => Number(el.getAttribute('variant-id'))));
    if (!onPage.size) return [];

    const o = this.outcomes;
    const sk = this.answers.skin_tone;
    const ey = this.answers.eye_color;
    const br = this.answers.brow_contrast;
    const combo = o && o[sk] && o[sk][ey] && o[sk][ey][br];
    if (combo && Array.isArray(combo.recommended)) {
      const ordered = combo.recommended
        .slice()
        .sort((a, b) => a.order - b.order)
        .filter((r) => onPage.has(Number(r.variantId)))
        .map((r) => ({ variantId: Number(r.variantId), text: r.text || '' }));
      if (ordered.length) return ordered;
    }
    // No real outcome resolved — fall back to the deterministic mock so the result
    // screen is never empty. Warn loudly so a config gap (missing/blank results_json,
    // an unmapped combo, or none of the combo's variants placed on the page) surfaces
    // in dev/QA instead of masquerading as a real recommendation.
    console.warn(
      `[lp-product-finder] no outcome for skin_tone=${sk} / eye_color=${ey} / brow_contrast=${br}` +
        `${o ? '' : ' (no outcomes data loaded)'} — using deterministic fallback`,
    );
    return this.mockRecommend();
  }

  // Deterministic, answer-dependent fallback when no outcomes data resolves.
  mockRecommend() {
    const ids = this.candidates().map((el) => Number(el.getAttribute('variant-id')));
    if (!ids.length) return [];
    const key = Object.keys(this.answers)
      .sort()
      .map((k) => k + ':' + this.answers[k])
      .join('|');
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    const start = hash % ids.length;
    const n = Math.min(6, ids.length);
    const out = [];
    for (let i = 0; i < n; i++) out.push({ variantId: ids[(start + i) % ids.length], text: '' });
    return out;
  }

  // Drifting 4-star twinkle on the result CTA: wander each star within a small
  // radius while CSS runs the scale/rotate/fade. Skipped under reduced motion.
  startTwinkle() {
    const wrap = this.twinkleEl;
    if (!wrap) return;
    const stars = Array.from(wrap.querySelectorAll('i'));
    if (!stars.length) return;
    const R = 12;
    const place = (el) => {
      const a = Math.random() * 6.2832;
      const r = R * Math.sqrt(Math.random());
      el.style.setProperty('--tx', (Math.cos(a) * r).toFixed(1) + 'px');
      el.style.setProperty('--ty', (Math.sin(a) * r).toFixed(1) + 'px');
    };
    stars.forEach((el, i) => {
      place(el);
      const b = el.querySelector('b');
      if (b) b.style.animationDelay = (i * 0.37).toFixed(2) + 's';
    });
    if (this.reduceMotion) return; // CSS shows the stars static; no drift loop
    this.stopTwinkle();
    this.twinkleTimer = window.setInterval(() => {
      stars.forEach((el) => {
        if (Math.random() < 0.65) place(el);
      });
    }, 680);
  }

  stopTwinkle() {
    if (this.twinkleTimer) {
      window.clearInterval(this.twinkleTimer);
      this.twinkleTimer = null;
    }
  }

  // Re-layout the LP Products section into the result and store the run for the
  // cart attributes. Fired automatically when the result screen appears (no scroll).
  dispatchComplete() {
    // finderId namespaces the cart attributes (there may be several finders). Set
    // via data-finder-id on the element; defaults to "lp_shade".
    const finderId = this.getAttribute('data-finder-id') || 'lp_shade';
    document.dispatchEvent(
      new CustomEvent('lpv2-finder:complete', {
        bubbles: true,
        detail: {
          finderId: finderId,
          answers: this.answers, // { axis: value } — traits for the cart attributes
          answerDetails: this.buildAnswerDetails(), // [{ axis, dim, value, label, swatch }] for pills
          recommended: this.recommended, // ordered [{ variantId, text }] — drives the result layout
        },
      }),
    );
  }

  // The result CTA. The layout is already applied (dispatchComplete on showResult),
  // so this only scrolls the customer down to the (off-screen) result.
  apply() {
    document.dispatchEvent(
      new CustomEvent('lpv2-finder:scroll', {
        bubbles: true,
        detail: { finderId: this.getAttribute('data-finder-id') || 'lp_shade' },
      }),
    );
  }

  // Tell the LP Products section to restore its default layout (Start over / re-run).
  dispatchReset() {
    document.dispatchEvent(
      new CustomEvent('lpv2-finder:reset', {
        bubbles: true,
        detail: { finderId: this.getAttribute('data-finder-id') || 'lp_shade' },
      }),
    );
  }

  // Ordered (by step) answer detail for the result choice pills.
  buildAnswerDetails() {
    return this.steps
      .map((step) => {
        const axis = step.dataset.axis;
        const meta = this.answerMeta[axis];
        if (!meta) return null;
        return {
          axis: axis,
          dim: meta.dim,
          value: this.answers[axis],
          label: meta.label,
          swatch: meta.swatch,
        };
      })
      .filter(Boolean);
  }
}

  customElements.define('lp-product-finder', LpProductFinder);
})();
